import { customerMarketShopfrontApi, Site } from '@envato/foundation-types'
import { match } from 'react-router-dom'
import { ThunkAction } from 'redux-thunk'
import uniq from 'lodash-es/uniq'
import isEmpty from 'lodash-es/isEmpty'

import { setSeoMeta } from '../actions/common'
import { ICategoryPageResult } from '../reducers/categoryPage'
import { reportPageType } from '../analytics'
import { reportElementsProductsImpressions } from '../analytics/categoryPageAnalytics'
import { reportProductsImpressions } from '../analytics/resultAnalytics'
import { getBookmarksForItems } from '../actions/user/bookmark'
import { IRootState } from '../reducers'
import axiosVerbose from '../utils/axios/axiosVerbose'
import { logError } from '../utils/errors'
import { hasFeature } from '../utils/features'
import { ICommonLocation, ILocationParams } from '../utils/location/types'
import { getSearchLocationParams } from '../utils/location'

export enum TypeKeys {
  GET_CATEGORY_PAGE_API_REQUEST = 'GET_CATEGORY_PAGE_API_REQUEST',
  GET_CATEGORY_PAGE_API_SUCCESS = 'GET_CATEGORY_PAGE_API_SUCCESS',
  GET_CATEGORY_PAGE_API_FAIL = 'GET_CATEGORY_PAGE_API_FAIL'
}

export interface IGetCategoryPageApiRequestAction {
  type: TypeKeys.GET_CATEGORY_PAGE_API_REQUEST
}

export interface IGetCategoryPageApiSuccessAction {
  type: TypeKeys.GET_CATEGORY_PAGE_API_SUCCESS
  payload: Readonly<ICategoryPageResult>
}

export interface IGetCategoryPageApiFailAction {
  type: TypeKeys.GET_CATEGORY_PAGE_API_FAIL
}

export type ActionTypes =
  | IGetCategoryPageApiRequestAction
  | IGetCategoryPageApiSuccessAction
  | IGetCategoryPageApiFailAction

const blocksThatContainItems = [
  'ItemsBlock',
  'PromoItemsBlock',
  'EnvatoPromoItemsBlock'
]
const blocksThatContainElementsItems = ['ElementsPromoItemsBlock']

export const blockContainsItems = (
  block: customerMarketShopfrontApi.categoryPage.ICategoryPageBlock
): block is customerMarketShopfrontApi.categoryPage.ICategoryPageBlock<
  customerMarketShopfrontApi.categoryPage.SearchItemsBlockData
> => {
  return (
    blocksThatContainItems.includes(block.componentType) &&
    block.data &&
    block.data.length > 0
  )
}

const getItemIdsFromBlocks = (
  blocks: customerMarketShopfrontApi.categoryPage.ICategoryPageBlock[]
) =>
  blocks
    .filter(blockContainsItems)
    .map(block => block.data)
    .reduce((items, data) => items.concat(data), [])
    .map(item => item.id)

const reportBlocks = (
  blocks: customerMarketShopfrontApi.categoryPage.ICategoryPageBlock[],
  currentSite: Site,
  category: string
) => {
  blocks.forEach(block => {
    if (blockContainsItems(block)) {
      reportProductsImpressions({
        items: block.data,
        currentSite,
        itemRankOffset: 1,
        page: 'Category Page',
        section: block.title
      })
    }

    if (blocksThatContainElementsItems.includes(block.componentType)) {
      reportElementsProductsImpressions({
        items: block.data,
        currentSite,
        itemRankOffset: 1,
        page: 'Category Page',
        section: 'Elements Promo',
        category
      })
    }
  })
}

export const onBeforeCategoryPageView = ({
  routeMatch
}: {
  routeMatch: match<any>
  prevLocation?: ICommonLocation
}): ThunkAction<void, IRootState, void> => (dispatch, getState) => {
  const currentSite = getState().common.currentSite
  const location = getState().router.location
  const experiments = getState().experiments

  const locationParams = getSearchLocationParams({
    location,
    routeMatch,
    currentSite
  })

  const isCategoryPageBlocksB = hasFeature(
    'categoryPageBlocksB',
    currentSite,
    location,
    experiments
  )

  const isCategoryPageBlocksC = hasFeature(
    'categoryPageBlocksC',
    currentSite,
    location,
    experiments
  )

  const isCategoryPageBlocksD = hasFeature(
    'categoryPageBlocksD',
    currentSite,
    location,
    experiments
  )

  let selectedBlocks

  if (isCategoryPageBlocksB) {
    selectedBlocks = getState().categoryPage.blocksB
  } else if (isCategoryPageBlocksC) {
    selectedBlocks = getState().categoryPage.blocksC
  } else if (isCategoryPageBlocksD) {
    selectedBlocks = getState().categoryPage.blocksD
  } else {
    selectedBlocks = getState().categoryPage.blocks
  }

  reportPageType('category', currentSite)

  reportBlocks(selectedBlocks, currentSite, locationParams.category || 'all')
}

export const fetchCategoryPageDataRequest = () => {
  const action: IGetCategoryPageApiRequestAction = {
    type: TypeKeys.GET_CATEGORY_PAGE_API_REQUEST
  }

  return action
}

export const fetchCategoryPageDataSuccess = (payload: any) => {
  const action: IGetCategoryPageApiSuccessAction = {
    payload,
    type: TypeKeys.GET_CATEGORY_PAGE_API_SUCCESS
  }

  return action
}

export const fetchCategoryPageDataFail = () => {
  const action: IGetCategoryPageApiFailAction = {
    type: TypeKeys.GET_CATEGORY_PAGE_API_FAIL
  }

  return action
}

const sortByBlockOrder = (
  blockA: customerMarketShopfrontApi.categoryPage.ICategoryPageBlock,
  blockB: customerMarketShopfrontApi.categoryPage.ICategoryPageBlock
): number => {
  if (blockA.order && blockB.order) {
    return blockA.order - blockB.order
  } else {
    return 0
  }
}

const transformBlocksToArray = (
  blocks:
    | customerMarketShopfrontApi.categoryPage.ICategoryPageBlock[]
    | customerMarketShopfrontApi.categoryPage.ICategoryPageBlocksDictionary
) => {
  if (!blocks) {
    return []
  }

  return Array.isArray(blocks)
    ? blocks
    : Object.values(blocks)
        .filter(value => !isEmpty(value))
        .sort(sortByBlockOrder)
}

export const fetchCategoryPageData = ({
  location,
  apiAppUrlPrefix,
  locationParams
}: {
  location: ICommonLocation
  apiAppUrlPrefix: string
  locationParams: ILocationParams
}): ThunkAction<Promise<ActionTypes>, IRootState, void> => {
  return (dispatch, getState) => {
    const { currentSite } = getState().common
    const category = locationParams.category || 'all'

    dispatch(fetchCategoryPageDataRequest())

    return axiosVerbose
      .get<customerMarketShopfrontApi.categoryPage.IResult>(
        `${apiAppUrlPrefix}/shopfront-api/category_page/${currentSite}/${category}`
      )
      .then(response => {
        const data = {
          ...response.data,
          blocks: transformBlocksToArray(response.data.blocks),
          blocksB: transformBlocksToArray(response.data.blocksB),
          blocksC: transformBlocksToArray(response.data.blocksC),
          blocksD: transformBlocksToArray(response.data.blocksD)
        }

        if (data.metadata) {
          dispatch(setSeoMeta(data.metadata))
        }

        // Temporary, for purpose of A/B/C/D experiment
        const itemIdsForBookmarks = uniq(
          [data.blocks, data.blocksB, data.blocksC, data.blocksD]
            .map(getItemIdsFromBlocks)
            .reduce((acc, value) => acc.concat(value), [])
        )

        dispatch(getBookmarksForItems(itemIdsForBookmarks))

        return dispatch(fetchCategoryPageDataSuccess(data))
      })
      .catch(err => Promise.reject(logError(err)))
      .catch(() => {
        return dispatch(fetchCategoryPageDataFail())
      })
  }
}
